import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import videojs from 'video.js/dist/video.js'
import '@videojs/http-streaming'
import 'video.js/dist/video-js.css'
import CustomVideoControls from './CustomVideoControls'
import { startSession, endSession } from '../services/usageTracking'
import './EpisodeView.css'

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

const API_BASE = 'https://backend.alephmedia.ro/api'

function getIsPortrait() {
  if (typeof window === 'undefined') {
    return false
  }
  return window.innerHeight > window.innerWidth
}

const DEFAULT_EPISODE = {
  id: null,
  title: 'Episod',
  description: '',
  poster_image_16_9: null,
  poster_image_9_16: null,
  video_16_9_url: null,
  video_9_16_url: null,
  program: null
}

// Funcții pentru tracking progres vizionare
let lastSaveTime = 0
const SAVE_INTERVAL = 5000 // Salvează la fiecare 5 secunde

function saveWatchProgress(episode, currentTime, duration) {
  if (!episode || !episode.id || !episode.program?.id) return
  
  // Throttle: salvează doar la fiecare 5 secunde
  const now = Date.now()
  if (now - lastSaveTime < SAVE_INTERVAL) {
    return
  }
  lastSaveTime = now
  
  try {
    const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
    const programId = episode.program.id
    const episodeId = episode.id
    
    // Verifică dacă episodul este completat (95% sau mai mult)
    const isCompleted = duration > 0 && currentTime >= duration * 0.95
    
    if (!watchData[programId]) {
      watchData[programId] = {}
    }
    
    watchData[programId][episodeId] = {
      currentTime,
      duration,
      lastWatched: Date.now(),
      completed: isCompleted,
      programTitle: episode.program?.title || '',
      episodeTitle: episode.title || '',
      // Salvează poster-urile episodului dacă sunt disponibile
      poster_image_16_9: episode.poster_image_16_9 || watchData[programId][episodeId]?.poster_image_16_9,
      poster_image_9_16: episode.poster_image_9_16 || watchData[programId][episodeId]?.poster_image_9_16
    }
    
    localStorage.setItem('watchProgress', JSON.stringify(watchData))
  } catch (error) {
    console.error('Eroare la salvarea progresului:', error)
  }
}

function markEpisodeAsCompleted(episode) {
  if (!episode || !episode.id || !episode.program?.id) return
  
  try {
    const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
    const programId = episode.program.id
    const episodeId = episode.id
    
    if (!watchData[programId]) {
      watchData[programId] = {}
    }
    
    if (watchData[programId][episodeId]) {
      watchData[programId][episodeId].completed = true
      watchData[programId][episodeId].lastWatched = Date.now()
      localStorage.setItem('watchProgress', JSON.stringify(watchData))
    }
  } catch (error) {
    console.error('Eroare la marcarea episodului ca terminat:', error)
  }
}

const EpisodeView = () => {
  const { id, episodeId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const playerRef = useRef(null)
  const videoNodeRef = useRef(null)
  const hideControlsTimeoutRef = useRef(null)
  const wasPlayingBeforeHoverRef = useRef(false) // Salvează starea de play înainte de hover
  const [episode, setEpisode] = useState(DEFAULT_EPISODE)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPortrait, setIsPortrait] = useState(getIsPortrait())
  const [isPlaying, setIsPlaying] = useState(false) // Episodes don't autoplay
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(NaN)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [airPlayAvailable, setAirPlayAvailable] = useState(false)
  const [isTitleHovered, setIsTitleHovered] = useState(false)
  const usageSessionIdRef = useRef(null) // ID-ul sesiunii de tracking
  const playStartTimeRef = useRef(null) // Timpul când a început play-ul

  useEffect(() => {
    if (!id || !episodeId) {
      navigate('/v3.0')
    }
  }, [id, episodeId, navigate])

  useEffect(() => {
    const updateOrientation = () => {
      setIsPortrait(getIsPortrait())
    }

    updateOrientation()
    window.addEventListener('resize', updateOrientation)
    window.addEventListener('orientationchange', updateOrientation)

    return () => {
      window.removeEventListener('resize', updateOrientation)
      window.removeEventListener('orientationchange', updateOrientation)
    }
  }, [])

  // Funcție pentru obținerea progresului salvat pentru un episod
  function getSavedProgress(programId, episodeId) {
    try {
      const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
      if (watchData[programId] && watchData[programId][episodeId]) {
        const episodeData = watchData[programId][episodeId]
        // Returnăm currentTime doar dacă episodul nu este completat
        if (!episodeData.completed && episodeData.currentTime > 0) {
          return episodeData.currentTime
        }
      }
    } catch (error) {
      console.error('Eroare la citirea progresului:', error)
    }
    return null
  }

  useEffect(() => {
    const stateEpisode = location.state?.episode
    const stateProgram = location.state?.program
    const resumeFrom = location.state?.resumeFrom
    
    if (stateEpisode) {
      setEpisode({ ...DEFAULT_EPISODE, ...stateEpisode, program: stateProgram || stateEpisode.program || null })
    }

    async function loadEpisode() {
      if (!id || !episodeId) return
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/programs/${id}/episodes/${episodeId}`)
        if (!response.ok) {
          throw new Error(`Nu am putut încărca episodul. Cod: ${response.status}`)
        }
        const json = await response.json()
        if (!json.success || !json.data) {
          throw new Error('Răspuns invalid de la server.')
        }
        setEpisode({ ...DEFAULT_EPISODE, ...json.data })
      } catch (err) {
        console.error('loadEpisode failed', err)
        setError(err.message || 'Nu am putut încărca episodul selectat.')
      } finally {
        setLoading(false)
      }
    }

    loadEpisode()
  }, [id, episodeId, location.state])

  useEffect(() => {
    const player = playerRef.current
    return () => {
      if (player) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [])

  const isLive = useMemo(() => !Number.isFinite(duration) || duration === 0, [duration])
  const progressPercent = useMemo(() => {
    if (isLive || duration <= 0) return 0
    return Math.max(0, Math.min(100, (currentTime / duration) * 100))
  }, [currentTime, duration, isLive])
  const volumePercent = useMemo(() => Math.max(0, Math.min(100, volume * 100)), [volume])
  const volumeIcon = useMemo(() => {
    if (isMuted || volumePercent === 0) {
      return 'muted'
    }
    if (volumePercent < 50) {
      return 'low'
    }
    return 'high'
  }, [isMuted, volumePercent])

  const selectSource = useCallback(() => {
    if (isPortrait && episode.video_9_16_url) {
      return { src: episode.video_9_16_url, type: 'application/x-mpegURL' }
    }
    if (episode.video_16_9_url) {
      return { src: episode.video_16_9_url, type: 'application/x-mpegURL' }
    }
    if (episode.video_9_16_url) {
      return { src: episode.video_9_16_url, type: 'application/x-mpegURL' }
    }
    return null
  }, [episode.video_16_9_url, episode.video_9_16_url, isPortrait])

  const canSeek = !isLive && Number.isFinite(duration) && duration > 0

  const formatTime = useCallback((time) => {
    if (!Number.isFinite(time) || time < 0) return '0:00'
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = Math.floor(time % 60)
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }, [])

  const handleTogglePlay = useCallback(() => {
    if (!playerRef.current) return
    if (isPlaying) {
      playerRef.current.pause()
    } else {
      playerRef.current.play()
    }
  }, [isPlaying])

  const handleToggleMute = useCallback(() => {
    if (!playerRef.current) return
    playerRef.current.muted(!isMuted)
  }, [isMuted])

  const handleVolumeChange = useCallback((newVolume) => {
    const player = playerRef.current
    if (!player) return
    player.volume(newVolume)
    setVolume(newVolume)
    if (newVolume === 0) {
      player.muted(true)
      setIsMuted(true)
    } else if (isMuted) {
      player.muted(false)
      setIsMuted(false)
    }
  }, [isMuted])

  const handleSeek = useCallback((newTime) => {
    const player = playerRef.current
    if (!player || !canSeek) return
    player.currentTime(newTime)
  }, [canSeek])

  const handleToggleFullscreen = useCallback(() => {
    if (!playerRef.current) return
    if (isFullscreen) {
      if (playerRef.current.exitFullscreen) {
        playerRef.current.exitFullscreen()
      }
    } else {
      if (playerRef.current.requestFullscreen) {
        playerRef.current.requestFullscreen()
      }
    }
  }, [isFullscreen])

  const handleAirPlay = useCallback(() => {
    if (!playerRef.current) return
    try {
      // Folosim o metodă mai sigură pentru AirPlay
      const videoEl = playerRef.current.el()?.querySelector('video')
      if (videoEl && typeof videoEl.webkitShowPlaybackTargetPicker === 'function') {
        videoEl.webkitShowPlaybackTargetPicker()
      } else if (playerRef.current.tech && playerRef.current.tech().hls && playerRef.current.tech().hls.airplay) {
        playerRef.current.tech().hls.airplay.requestAirplay()
      }
    } catch (err) {
      console.warn('[EpisodeView] AirPlay error:', err)
    }
  }, [])

  const ensureControlsVisible = useCallback(() => {
    setControlsVisible(true)
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current)
    }
    // Nu ascunde controalele dacă titlul este hover-at
    if (!isTitleHovered) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false)
      }, 3000)
    }
  }, [isTitleHovered])

  const handleMouseMove = useCallback(() => {
    ensureControlsVisible()
  }, [ensureControlsVisible])

  const handleTitleHoverChange = useCallback((isHovered) => {
    setIsTitleHovered(isHovered)
    
    const player = playerRef.current
    if (!player) {
      return
    }
    
    const isPaused = player.paused()
    
    if (isHovered) {
      // Salvează starea curentă de play când intră mouse-ul pe titlu
      wasPlayingBeforeHoverRef.current = !isPaused
      
      // Anulează timer-ul de ascundere
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
      }
    } else {
      // Când iese mouse-ul de pe titlu, verifică dacă trebuie să continue play-ul
      if (wasPlayingBeforeHoverRef.current && player.paused()) {
        player.play().catch(err => {
          console.warn('[EpisodeView] Could not resume playback:', err)
        })
      }
      
      // Reaplică regula de ascundere
      ensureControlsVisible()
    }
  }, [ensureControlsVisible])

  useEffect(() => {
    // Așteaptă ca elementul video să fie montat în DOM
    if (!videoNodeRef.current) {
      // Nu logăm warning dacă componenta încă se încarcă
      if (!loading && episode.id) {
        console.warn('[EpisodeView] videoNodeRef este null')
      }
      return
    }

    const source = selectSource()
    if (!source) {
      if (!loading) {
        console.warn('[EpisodeView] Nu am găsit sursă pentru episod', episode)
      }
      return
    }


    if (!playerRef.current) {
      const playerOpts = {
        autoplay: true,
        controls: false,
        muted: false,
        preload: 'auto',
        html5: {
          nativeAudioTracks: false,
          nativeVideoTracks: false
        },
        liveui: false,
        inactivityTimeout: 0,
        fluid: false,
        responsive: true
      }

      if (!isSafari) {
        playerOpts.html5.vhs = {
          overrideNative: true,
          enableLowInitialPlaylist: true,
          limitRenditionByPlayerDimensions: true,
          smoothQualityChange: true,
          bandwidth: 5000000,
          backBufferLength: 30,
          bufferBasedABR: true,
          experimentalBufferBasedABR: true,
          experimentalLeastPixelDiffSelector: true,
          maxPlaylistRetries: 5,
          maxPlaylistRetryDelay: 10000,
          maxSegmentRetries: 3,
          maxSegmentRetryDelay: 5000,
          maxBufferLength: 30,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5
        }
      }

      playerRef.current = videojs(videoNodeRef.current, playerOpts)

      const player = playerRef.current

      const updateMetrics = () => {
        setIsPlaying(!player.paused())
        setIsMuted(player.muted())
        const currentVolume = player.volume()
        setVolume(currentVolume)
        const dur = player.duration()
        setDuration(dur)
        setCurrentTime(player.currentTime())
      }

      player.ready(() => {
        // Event listeners pentru player
        player.on('play', () => {
          setIsPlaying(true)
          // Începem tracking-ul când începe play-ul
          if (!usageSessionIdRef.current && episode.id && episode.program?.id) {
            usageSessionIdRef.current = startSession('episode', episode.id, {
              program_id: episode.program.id,
              program_title: episode.program.title || '',
              episode_title: episode.title || ''
            })
            playStartTimeRef.current = Date.now()
          }
        })
        player.on('pause', () => {
          setIsPlaying(false)
          // Salvează progresul când se face pause
          saveWatchProgress(episode, player.currentTime(), player.duration())
          
          // Finalizăm tracking-ul dacă există
          if (usageSessionIdRef.current && playStartTimeRef.current) {
            endSession(usageSessionIdRef.current)
            usageSessionIdRef.current = null
            playStartTimeRef.current = null
          }
          
          // Dacă titlul este hover-at și era în play, repornește automat
          setTimeout(() => {
            if (wasPlayingBeforeHoverRef.current && document.querySelector('.control-title--expanded')) {
              player.play().catch(err => {
                console.warn('[EpisodeView] Could not auto-resume playback:', err)
              })
            }
          }, 100)
        })
        player.on('ended', () => {
          setIsPlaying(false)
          // Marchează episodul ca terminat
          markEpisodeAsCompleted(episode)
          
          // Finalizăm tracking-ul
          if (usageSessionIdRef.current) {
            endSession(usageSessionIdRef.current)
            usageSessionIdRef.current = null
            playStartTimeRef.current = null
          }
        })
        player.on('seeking', () => {
          // Seaking event
        })
        player.on('seeked', () => {
          // Salvează progresul imediat după seek
          saveWatchProgress(episode, player.currentTime(), player.duration())
        })
        player.on('volumechange', () => {
          setVolume(player.volume())
          setIsMuted(player.muted())
        })
        player.on('timeupdate', () => {
          const newTime = player.currentTime()
          setCurrentTime(newTime)
          // Salvează progresul în localStorage
          saveWatchProgress(episode, newTime, player.duration())
        })
        player.on('durationchange', () => setDuration(player.duration()))
        player.on('fullscreenchange', () => setIsFullscreen(player.isFullscreen()))
        // Flag pentru a evita să setăm timpul de mai multe ori
        let resumeTimeSet = false
        
        player.on('loadedmetadata', () => {
          setDuration(player.duration())
          setVolume(player.volume())
          setIsMuted(player.muted())
          
          // Continuă de unde a rămas dacă există progres salvat
          if (!resumeTimeSet && id && episodeId) {
            const savedTime = location.state?.resumeFrom || getSavedProgress(parseInt(id), parseInt(episodeId))
            if (savedTime && savedTime > 0 && player.duration() > savedTime) {
              player.currentTime(savedTime)
              setCurrentTime(savedTime)
              resumeTimeSet = true
            }
          }
          
          // Pornește play-ul după ce metadata este încărcată
          player.play().catch(err => {
            // Dacă autoplay eșuează (de ex. din cauza politicilor browser-ului), ignorăm eroarea
            console.warn('[EpisodeView] Autoplay failed, user interaction required:', err)
          })
        })
        
        // Event listener pentru canplay - alternativ pentru a continua
        player.on('canplay', () => {
          if (!resumeTimeSet && id && episodeId) {
            const savedTime = location.state?.resumeFrom || getSavedProgress(parseInt(id), parseInt(episodeId))
            if (savedTime && savedTime > 0 && player.duration() > savedTime && player.currentTime() < savedTime - 1) {
              // Doar dacă nu am setat deja timpul
              player.currentTime(savedTime)
              setCurrentTime(savedTime)
              resumeTimeSet = true
            }
          }
        })

        const videoEl = player.el()?.querySelector('video')
        if (videoEl && typeof videoEl.webkitShowPlaybackTargetPicker === 'function') {
          setAirPlayAvailable(true)
        }

        // Adaugă listener pentru click pe video care ar putea cauza pause
        const playerEl = player.el()
        if (playerEl) {
          playerEl.addEventListener('click', (e) => {
            // Click handler
          })
        }

        player.src(source)
        updateMetrics()
        ensureControlsVisible()
      })

      player.on('error', () => {
        const error = player.error()
        console.error('[EpisodeView] Player error', error)
      })
    } else {
      const player = playerRef.current
      // Nu reîncărcăm sursa dacă e aceeași - asta cauza resetarea!
      const currentSrc = player.currentSrc()
      if (currentSrc && currentSrc.includes(source.src.split('?')[0])) {
        return
      }
      player.src(source)
      // Pornește play-ul când sursa este setată și metadata este încărcată
      player.on('loadedmetadata', () => {
        player.play().catch(err => {
          // Dacă autoplay eșuează, ignorăm eroarea
          console.warn('[EpisodeView] Autoplay failed on source change:', err)
        })
      })
      if (isSafari) {
        const videoEl = player.el()?.querySelector('video')
        if (videoEl) {
          videoEl.src = source.src
        }
      }
    }

    const posterUrl = isPortrait ? episode.poster_image_9_16 || episode.poster_image_16_9 : episode.poster_image_16_9 || episode.poster_image_9_16
    if (playerRef.current && posterUrl) {
      playerRef.current.poster(posterUrl)
    }

    return () => {
      // Cleanup: finalizăm sesiunea de tracking dacă există
      if (usageSessionIdRef.current) {
        endSession(usageSessionIdRef.current)
        usageSessionIdRef.current = null
        playStartTimeRef.current = null
      }
    }
  }, [selectSource, episode, isPortrait, loading, id, episodeId, location.state])

  const rootClassName = useMemo(() => {
    return `episode-view-root${isPortrait ? ' episode-view-root--portrait' : ''}`
  }, [isPortrait])

  const poster = isPortrait ? episode.poster_image_9_16 || episode.poster_image_16_9 || null : episode.poster_image_16_9 || episode.poster_image_9_16 || null

  return (
    <div className={rootClassName} onMouseMove={handleMouseMove} onTouchStart={handleMouseMove}>
      {loading && <div className="episode-view-status">Se încarcă episodul...</div>}
      {error && <div className="episode-view-status episode-view-status-error">{error}</div>}

      {!loading && !error && episode && (
        <div className={`episode-view-player ${controlsVisible ? 'episode-view-player--active' : ''}`}>
          <div className="episode-view-player-inner">
            <video
              ref={videoNodeRef}
              className="video-js"
              playsInline
              poster={poster || undefined}
            />
          </div>

          {controlsVisible && (
            <CustomVideoControls
              isPlaying={isPlaying}
              isMuted={isMuted}
              volume={volume}
              currentTime={currentTime}
              duration={duration}
              isFullscreen={isFullscreen}
              streamName={episode.title}
              description={episode.description}
              onPlayPause={handleTogglePlay}
              onMuteToggle={handleToggleMute}
              onVolumeChange={handleVolumeChange}
              onSeek={handleSeek}
              onFullscreenToggle={handleToggleFullscreen}
              onAirplayToggle={handleAirPlay}
              onBack={() => navigate(-1)}
              onTitleHoverChange={handleTitleHoverChange}
            />
          )}

          {/* Old controls are now hidden via CSS */}
          <div className={`episode-view-controls episode-view-controls-hidden ${controlsVisible ? 'episode-view-controls--visible' : ''}`}>
            <div className="episode-view-controls-left">
              <button type="button" className="episode-view-control-btn" onClick={handleTogglePlay}>
                {isPlaying ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              <button type="button" className="episode-view-control-btn" onClick={handleToggleMute}>
                {volumeIcon === 'muted' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
                {volumeIcon === 'low' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
                {volumeIcon === 'high' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>

              <div className="episode-view-volume">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  onInput={handleVolumeChange}
                  style={{ '--progress': `${volumePercent}%` }}
                />
              </div>
            </div>

            <div className="episode-view-controls-center">
              {canSeek ? (
                <div className="episode-view-timeline">
                  <span>{formatTime(currentTime)}</span>
                  <input
                    type="range"
                    min="0"
                    max={Number.isFinite(duration) ? duration : 0}
                    step="0.1"
                    value={Math.min(currentTime, duration || 0)}
                    onChange={handleSeek}
                    onInput={handleSeek}
                    style={{ '--progress': `${progressPercent}%` }}
                  />
                  <span>{formatTime(duration)}</span>
                </div>
              ) : (
                <div className="episode-view-live-indicator">
                  <span className="live-dot" />LIVE
                </div>
              )}
            </div>

            <div className="episode-view-controls-right">
              {airPlayAvailable && (
                <button type="button" className="episode-view-control-btn" onClick={handleAirPlay}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 19V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14" />
                    <polygon points="7 20 12 15 17 20" />
                  </svg>
                </button>
              )}
              <button type="button" className="episode-view-control-btn" onClick={handleToggleFullscreen}>
                {isFullscreen ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 9 9 5 5 5" />
                    <polyline points="15 9 15 5 19 5" />
                    <polyline points="9 15 9 19 5 19" />
                    <polyline points="15 15 15 19 19 19" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EpisodeView
